<div class="role8-form-container">
  <p-toast></p-toast>

  <!-- Loading State (sibling of p-card so it is visible while schema/form are being set up) -->
  <app-sheet-loader [visible]="isLoading || !schema || !form" />

  <p-card
    [header]="formTitle"
    [subheader]="isDev ? schema.label : ''"
    *ngIf="!isLoading && schema && form"
    class="form-card"
  >
    <form [formGroup]="form">
      <!-- Section 1: Disclosure Header (NO TextBlock) -->
      <ng-container *ngFor="let control of schema.controls; let controlIndex = index">
        <ng-container *ngIf="controlIndex === 0 && control.type === 'TextBlock'">
          <!-- SKIP the TextBlock - only show the section header before it -->
        </ng-container>
      </ng-container>

      <!-- Find and display the section header before the table -->
      <div
        class="bg-gray-200 text-black-700 text-sm font-medium px-2 py-1 mt-3 mb-3"
      >
        Disclosure on statement of financial position
      </div>

      <!-- Section 2: Financial Statement Table -->
      <div class="financial-statement-table-wrapper mt-4 viewport-sticky">
        <table
          class="financial-statement-table"
          appFieldLinkTarget
          fieldLinkSheet="statement-of-financial-position"
          [fieldLinkControls]="schema.controls"
          [fieldLinkSections]="expandedSections"
          [fieldLinkIsSectionHeader]="isSectionHeaderFn"
        >
          <thead>
            <!-- Top merged header row -->
            <tr>
              <th class="description-column" rowspan="2">Fields</th>
              <!-- Consolidated header - show only if Consolidated is enabled -->
              <th
                *ngIf="showConsolidatedCurrent || showConsolidatedPrevious"
                class="amount-column merged-header"
                [attr.colspan]="
                  (showConsolidatedCurrent ? 1 : 0) +
                  (showConsolidatedPrevious ? 1 : 0)
                "
              >
                Consolidated
              </th>
              <!-- Separate header - calculate colspan based on visible columns -->
              <th
                class="amount-column merged-header"
                [attr.colspan]="
                  (showSeparateCurrent ? 1 : 0) + (showSeparatePrevious ? 1 : 0)
                "
              >
                Separate
              </th>
              <th class="actions-column" rowspan="2">Actions</th>
              <th class="validation-status-column" rowspan="2">
                Mapping Status
              </th>
              <th class="validation-error-column" rowspan="2">
                Error Description
              </th>
            </tr>
            <!-- Individual column headers -->
            <tr>
              <!-- Consolidated Current -->
              <th *ngIf="showConsolidatedCurrent" class="amount-column">
                <div>
                  {{ periodHeaderCurrent }}
                </div>
                <div>(Current)</div>
              </th>
              <!-- Consolidated Previous -->
              <th *ngIf="showConsolidatedPrevious" class="amount-column">
                <div>
                  {{ periodHeaderPrevious }}
                </div>
                <div>(Previous)</div>
              </th>
              <!-- Separate Current -->
              <th *ngIf="showSeparateCurrent" class="amount-column">
                <div>
                  {{ periodHeaderCurrent }}
                </div>
                <div>(Current)</div>
              </th>
              <!-- Separate Previous -->
              <th *ngIf="showSeparatePrevious" class="amount-column">
                <div>
                  {{ periodHeaderPrevious }}
                </div>
                <div>(Previous)</div>
              </th>
            </tr>
          </thead>
          <tbody>
            <ng-container
              *ngFor="let control of schema.controls; let controlIndex = index"
            >
              <!-- Skip the first TextBlock -->
              <ng-container *ngIf="!(controlIndex === 0 && control.type === 'TextBlock')">
                <!-- Section Headers (ALL LEVELS: 1, 2, 3, 4, 5) -->
                <tr
                  *ngIf="isSectionHeader(control) && shouldShowRow(control, controlIndex)"
                  [ngClass]="getHeaderClass(control.ui.level)"
                  (click)="
                    isCollapsibleHeader(controlIndex)
                      ? toggleSection(control.qname)
                      : null
                  "
                >
                  <td
                    [attr.colspan]="1 + totalDataColumns + 3"
                    class="section-header"
                  >
                    <h3>
                      <i
                        *ngIf="isCollapsibleHeader(controlIndex)"
                        [class]="
                          isSectionExpanded(control.qname)
                            ? 'pi pi-chevron-down'
                            : 'pi pi-chevron-right'
                        "
                      >
                      </i>
                      {{ control.label }}
                    </h3>
                  </td>
                </tr>

                <!-- Table Data Rows -->
                <tr
                  *ngIf="isMonetaryItem(control) && shouldShowRow(control, controlIndex)"
                  class="data-row"
                  [attr.data-qname]="control.qname"
                  [class.row-has-error]="getRowColorIntent(control) === 'error'"
                  [class.row-has-validation-error]="getRowColorIntent(control) === 'warning'"
                  [class.calculated-row]="isCalculatedField(control)"
                >
                  <!-- Description Column - Clickable when has inheritance link -->
                  <td class="description-column">
                    <app-field-link-label [control]="control" /><app-required-marker [when]="isFieldRequired(control)" />
                  </td>

                  <!-- Consolidated - Current Year -->
                  <td *ngIf="showConsolidatedCurrent" class="amount-column">
                    <p-inputnumber appSchemaNumeric [schemaControl]="control" appNegativeHighlight
                      [formControlName]="
                        getXbrlControlName(
                          control.qname,
                          'Consolidated',
                          'Current'
                        )
                      "
                      [ngClass]="{'input-has-error': hasColumnError(control, 'Consolidated', 'Current')}"
                      mode="decimal"
                      [minFractionDigits]="numericPolicy(control).decimals"
                      [maxFractionDigits]="numericPolicy(control).decimals"
                      [useGrouping]="numericPolicy(control).grouping"
                      [style]="{ width: '100%' }"
                    >
                    </p-inputnumber>
                  </td>

                  <!-- Consolidated - Previous Year -->
                  <td *ngIf="showConsolidatedPrevious" class="amount-column">
                    <p-inputnumber appSchemaNumeric [schemaControl]="control" appNegativeHighlight
                      [formControlName]="
                        getXbrlControlName(
                          control.qname,
                          'Consolidated',
                          'Previous'
                        )
                      "
                      [ngClass]="{'input-has-error': hasColumnError(control, 'Consolidated', 'Previous')}"
                      mode="decimal"
                      [minFractionDigits]="numericPolicy(control).decimals"
                      [maxFractionDigits]="numericPolicy(control).decimals"
                      [useGrouping]="numericPolicy(control).grouping"
                      [style]="{ width: '100%' }"
                    >
                    </p-inputnumber>
                  </td>

                  <!-- Separate - Current Year -->
                  <td *ngIf="showSeparateCurrent" class="amount-column">
                    <p-inputnumber appSchemaNumeric [schemaControl]="control" appNegativeHighlight
                      [formControlName]="
                        getXbrlControlName(control.qname, 'Separate', 'Current')
                      "
                      [ngClass]="{'input-has-error': hasColumnError(control, 'Separate', 'Current')}"
                      mode="decimal"
                      [minFractionDigits]="numericPolicy(control).decimals"
                      [maxFractionDigits]="numericPolicy(control).decimals"
                      [useGrouping]="numericPolicy(control).grouping"
                      [style]="{ width: '100%' }"
                    >
                    </p-inputnumber>
                  </td>

                  <!-- Separate - Previous Year -->
                  <td *ngIf="showSeparatePrevious" class="amount-column">
                    <p-inputnumber appSchemaNumeric [schemaControl]="control" appNegativeHighlight
                      [formControlName]="
                        getXbrlControlName(
                          control.qname,
                          'Separate',
                          'Previous'
                        )
                      "
                      [ngClass]="{'input-has-error': hasColumnError(control, 'Separate', 'Previous')}"
                      mode="decimal"
                      [minFractionDigits]="numericPolicy(control).decimals"
                      [maxFractionDigits]="numericPolicy(control).decimals"
                      [useGrouping]="numericPolicy(control).grouping"
                      [style]="{ width: '100%' }"
                    >
                    </p-inputnumber>
                  </td>

                  <!-- Actions Column - Pencil/Tick/Cross Icons -->
                  <td class="actions-column">
                    <ng-container
                      *ngIf="
                        !isCalculatedField(control) && !control.ui.readonly
                      "
                    >
                      <!-- Pencil SVG Icon (Default - Not in Edit Mode) -->
                      <svg
                        *ngIf="isValidationComplete && !isRowInEditMode(control)"
                        class="action-icon pencil-icon"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        (click)="enterEditMode(control)"
                        title="Edit"
                      >
                        <path
                          fill-rule="evenodd"
                          clip-rule="evenodd"
                          d="M12.1657 1.94834C12.3339 1.77238 12.5621 1.67352 12.8 1.67352C12.9178 1.67352 13.0345 1.69779 13.1433 1.74494C13.2521 1.7921 13.351 1.86121 13.4343 1.94834C13.5176 2.03547 13.5837 2.1389 13.6288 2.25274C13.6739 2.36657 13.6971 2.48858 13.6971 2.6118C13.6971 2.73502 13.6739 2.85703 13.6288 2.97086C13.5837 3.0847 13.5176 3.18814 13.4343 3.27526L3.59101 13.5709L1.89951 14.0132L2.32239 12.244L12.1657 1.94834ZM12.8 0C12.1377 0 11.5026 0.275171 11.0343 0.764979L1.03433 11.2245C0.931804 11.3317 0.859069 11.4661 0.823902 11.6132L0.0239029 14.9603C-0.044252 15.2454 0.0356272 15.5471 0.234331 15.7549C0.433035 15.9627 0.721425 16.0463 0.994044 15.975L4.19404 15.1382C4.33471 15.1015 4.46317 15.0254 4.5657 14.9181L14.5657 4.45862C14.7976 4.21609 14.9815 3.92817 15.107 3.61129C15.2325 3.29441 15.2971 2.95479 15.2971 2.6118C15.2971 2.26881 15.2325 1.92919 15.107 1.61231C14.9815 1.29543 14.7976 1.00751 14.5657 0.764979C14.3338 0.52245 14.0585 0.330067 13.7556 0.198811C13.4526 0.0675562 13.1279 0 12.8 0ZM8.00001 14.3265C7.55818 14.3265 7.20001 14.7011 7.20001 15.1632C7.20001 15.6254 7.55818 16 8.00001 16H15.2C15.6418 16 16 15.6254 16 15.1632C16 14.7011 15.6418 14.3265 15.2 14.3265H8.00001Z"
                          fill="currentColor"
                        />
                      </svg>

                      <!-- Tick and Cross SVG Icons (In Edit Mode - Same Line) -->
                      <div
                        *ngIf="isRowInEditMode(control)"
                        class="action-icons-group"
                      >
                        <!-- Tick/Check SVG Icon -->
                        <svg
                          class="action-icon tick-icon"
                          viewBox="0 0 16 16"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          (click)="saveEditMode(control)"
                          title="Save"
                        >
                          <path
                            d="M13.5 3.5L6 11L2.5 7.5"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                        </svg>

                        <!-- Cross/X SVG Icon -->
                        <svg
                          class="action-icon cross-icon"
                          viewBox="0 0 16 16"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          (click)="cancelEditMode(control)"
                          title="Cancel"
                        >
                          <path
                            d="M12 4L4 12M4 4L12 12"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                        </svg>
                      </div>
                    </ng-container>
                  </td>

                  <!-- Mapping Status -->
                  <td
                    class="validation-status-column"
                    [ngClass]="{
                      'status-failed': isValidationComplete && getRowValidationStatus(control) === 'Failed',
                      'status-validated-warning': isValidationComplete && getRowValidationStatus(control) === 'Validated*'
                    }"
                  >
                    <ng-container *ngIf="isValidationComplete; else validating">
                      <ng-container [ngSwitch]="getRowValidationStatus(control)">
                        <ng-container *ngSwitchCase="'Validated*'">
                          Validated<span class="status-asterisk">*</span>
                        </ng-container>
                        <ng-container *ngSwitchCase="'Failed'">
                          Failed
                        </ng-container>
                        <ng-container *ngSwitchDefault>
                          {{ getRowValidationStatus(control) }}
                        </ng-container>
                      </ng-container>
                    </ng-container>
                    <ng-template #validating>
                      <p-skeleton width="70%" height="1rem" />
                    </ng-template
                  ><app-required-marker [when]="isFieldRequired(control)" />
                  </td>

                  <!-- Error Description -->
                  <td class="validation-error-column" data-testid="error-description">
                    <ng-container *ngIf="isValidationComplete; else errorLoading">
                      <span [innerHTML]="getRowErrorDescription(control)"></span>
                    </ng-container>
                    <ng-template #errorLoading>
                      <p-skeleton width="100%" height="1rem" />
                    </ng-template
                  ><app-required-marker [when]="isFieldRequired(control)" />
                  </td>
                </tr>
              </ng-container>
            </ng-container>
          </tbody>
        </table>
      </div>
    </form>
    <submit-for-validation
      [form]="form"
      [sheetKey]="'statementOfFinancialPosition'"
      [formTitle]="formTitle"
      [evaluateSheetStatus]="evaluateSheetStatus.bind(this)"
      (cancelConfirmed)="onCancelConfirmed()"
    ></submit-for-validation>
  </p-card>
</div>
